import { inject, Injectable } from '@angular/core';

/**
 * libs
 */
import { saveAs } from 'file-saver';

/**
 * interfaces
 */
import { ExportFormat } from '../types';
import { ICsvExporter, ICsvExportRequest } from '../interfaces';

/**
 * utils
 */
import { ensureFileExtension, normalizeExportValue, resolveExportCellValue } from './export-row.utils';

/**
 * providers
 */
import { EXPORT_OPTIONS } from '../provider/export-options.token';
import { EXPORT_PROVIDER_DEFAULT_OPTIONS } from '../provider/export-provider-options';

/**
 * CSV export strategy implementation.
 */
@Injectable()
export class CsvExporterService implements ICsvExporter {

  /**
   * The format of the exporter.
   */
  public readonly format: ExportFormat = 'csv';

  /**
   * The options for the exporter.
   */
  private readonly _options = inject(EXPORT_OPTIONS, { optional: true }) ?? EXPORT_PROVIDER_DEFAULT_OPTIONS;

  /**
   * Export the data to a CSV file.
   *
   * @param request - The export request. Must carry `rows` and `columns`.
   * @throws When the request carries no `rows` / `columns` pair.
   *
   * @remarks
   * One table per file. `rows` and `columns` are optional on
   * {@link IExportRequest} only because other formats — an image, and a
   * sectioned PDF — do not use them; CSV always needs them, so their absence is rejected
   * here with a clear message rather than failing later as an unclear crash.
   *
   * `async` only to satisfy {@link IExporter}, whose `export` is
   * `Promise<void>` for every implementer. Nothing here is asynchronous.
   */
  public async export<TRow = unknown>(request: ICsvExportRequest<TRow>): Promise<void> {
    const { rows, columns } = request;

    if (rows == null || columns == null) {
      throw new Error('CSV export only supports a single table (rows + columns); multi-section export is not supported.');
    }

    const delimiter = this._options.csvDelimiter;
    const fileName = ensureFileExtension(request.fileName, '.csv');

    const headerLine = columns
      .map((column) => this._escapeCsvValue(column.header, delimiter))
      .join(delimiter);

    const dataLines = rows.map((row) => {
      const rowValues = columns.map((column) => {
        const value = resolveExportCellValue(row, column);
        return this._escapeCsvValue(normalizeExportValue(value), delimiter);
      });

      return rowValues.join(delimiter);
    });

    const csvContent = [headerLine, ...dataLines].join('\n');
    const bom = this._options.csvBom ? '\uFEFF' : '';
    const blob = new Blob([`${bom}${csvContent}`], { type: 'text/csv;charset=utf-8;' });

    saveAs(blob, fileName);
  }

  /**
   * Escape the CSV value.
   * @param value - The value to escape.
   * @param delimiter - The delimiter to escape.
   * @returns The escaped value.
   */
  private _escapeCsvValue(value: string | number | boolean, delimiter: string): string {
    const asString = String(value);
    const escaped = asString.replaceAll('"', '""');
    const mustQuote = escaped.includes(delimiter) || escaped.includes('\n') || escaped.includes('\r') || escaped.includes('"');

    return mustQuote ? `"${escaped}"` : escaped;
  }
}
